import { EQUIPMENT_STATUSES } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Modal, Select, Stack, Textarea } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck, IconSend } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Reauth, ReauthField, useReauth } from '../../approval-policy.js';
import { type MasterCapacity, MasterSectionNote, masterLocked } from '../../master-section.js';
import type { DefectPatch, DefectView } from './condition-types.js';
import type { RepairInfo } from './DefectDrawer.js';
import { type CloseFormValues, closeFormOf, closeFormSchema } from './defect-view.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import work from './PmsWork.module.css';

/**
 * Kapanış formunun gövdesi (bileşen testi sunucu çizimiyle): kök neden, düzeltici faaliyet, test,
 * onarım sonrası durum, onarım ya da gerekçe, klas bildirimi ve onay adımı (K24). Parola alanı
 * yalnız şirket ayarı açıkken ya da sunucu isterse görünür (ADR 0075).
 */
export function CloseFields({
  form,
  defect,
  classEquipment,
  repair,
  reauth,
  master,
}: {
  form: UseFormReturn<CloseFormValues>;
  defect: DefectView;
  classEquipment: boolean;
  repair: RepairInfo | null;
  reauth: Reauth;
  /** Part B'yi yazma sıfatı (ADR 0127 §2); eski sunucuda yok (kilit yok). */
  master?: MasterCapacity | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.defects.detail.${key}`);
  const error = (name: keyof CloseFormValues | 'repair') => {
    const message = (form.formState.errors as Record<string, { message?: string } | undefined>)[
      name
    ]?.message;
    return message
      ? t(`pms.errors.${message}`, {
          defaultValue: t(`pms.fieldErrors.${message}`, {
            defaultValue: t('pms.errors.VALIDATION_FAILED'),
          }),
        })
      : undefined;
  };
  const text = (name: keyof CloseFormValues, required = false, readOnly = false) => (
    <Textarea
      label={k(name)}
      required={required}
      autosize
      minRows={2}
      readOnly={readOnly}
      {...form.register(name)}
      error={error(name)}
    />
  );
  // Part B kaptanın (LGS-17; ADR 0127 §1, §2): kaptan değilse salt okunur; boşsa önce kaptan yazar
  const partBLocked = masterLocked(master);
  const repairError = error('repair');
  return (
    <Stack gap="sm">
      <p className={work.dialogBody}>{t('pms.condition.defects.close.body')}</p>
      {text('immediateAction', true, partBLocked)}
      <MasterSectionNote capacity={master} />
      {partBMissing(defect, master) ? (
        <Alert color="orange" icon={<IconAlertCircle size={16} />} role="alert">
          {t('masterSection.partBFirst')}
        </Alert>
      ) : null}
      <Controller
        control={form.control}
        name="procedureReview"
        render={({ field }) => (
          <Select
            label={t('pms.condition.defects.close.procedureReview')}
            required
            data={[
              { value: 'no', label: t('pms.condition.defects.close.procedureNo') },
              { value: 'yes', label: t('pms.condition.defects.close.procedureYes') },
            ]}
            value={field.value || null}
            onChange={(value) => field.onChange(value ?? '')}
            error={error('procedureReview')}
          />
        )}
      />
      {form.watch('procedureReview') === 'yes' ? (
        <>
          {text('procedure', true)}
          {text('improvement', true)}
        </>
      ) : null}
      {text('rootCause', true)}
      {text('correctiveAction', true)}
      {text('testResult')}
      <Controller
        control={form.control}
        name="conditionAfter"
        render={({ field }) => (
          <Select
            label={k('conditionAfter')}
            description={t('pms.condition.defects.close.conditionHint')}
            required
            data={EQUIPMENT_STATUSES.map((s) => ({ value: s, label: t(`pms.statuses.${s}`) }))}
            value={field.value || null}
            onChange={(value) => field.onChange(value ?? '')}
            error={error('conditionAfter')}
          />
        )}
      />
      {repair ? (
        <Alert
          color={repair.reported ? 'green' : 'orange'}
          icon={repair.reported ? <IconCircleCheck size={16} /> : <IconAlertCircle size={16} />}
        >
          {repair.reported
            ? t('pms.condition.defects.close.repairReported', { wo: repair.woNo })
            : t('pms.condition.defects.close.repairNotReported', { wo: repair.woNo })}
        </Alert>
      ) : (
        text('noRepairReason', true)
      )}
      {repairError ? (
        <p className={work.error} role="alert">
          {repairError}
        </p>
      ) : null}
      {defect.kind === 'breakdown' && classEquipment ? (
        <>
          <p className={work.note}>{k('classItem')}</p>
          {text('classNotificationRef')}
          {text('classNotificationWaiver')}
        </>
      ) : null}
      <ReauthField
        reauth={reauth}
        label={t('pms.work.password.password')}
        description={t('pms.work.password.hint')}
        mb="0"
      />
    </Stack>
  );
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

/** Part B boş ve kişi kaptan değil: önce kaptan doldurur, kapanışa gönderilemez (ADR 0127 §2). */
export function partBMissing(defect: DefectView, master: MasterCapacity | undefined): boolean {
  return masterLocked(master) && !defect.immediateAction?.trim();
}

/** Kapanış formunda değişen alanlar ve doğrulama adımı (K24). */
export function closePatchOf(defect: DefectView, values: CloseFormValues): DefectPatch {
  const before = closeFormOf(defect);
  const patch: Record<string, unknown> = {};
  const REVIEW = new Set(['procedureReview', 'procedure', 'improvement']);
  for (const key of Object.keys(values) as (keyof CloseFormValues)[]) {
    if (REVIEW.has(key) || values[key] === before[key]) continue;
    patch[key] = blank(values[key]);
  }
  // Prosedür gözden geçirme bütün olarak (ADR 0060 §9)
  if (
    values.procedureReview !== '' &&
    (values.procedureReview !== before.procedureReview ||
      values.procedure !== before.procedure ||
      values.improvement !== before.improvement)
  ) {
    const required = values.procedureReview === 'yes';
    patch.procedureReview = {
      required,
      procedure: required ? blank(values.procedure) : null,
      improvement: required ? blank(values.improvement) : null,
    };
  }
  if (defect.status !== 'verification') patch.status = 'verification';
  return patch as DefectPatch;
}

/**
 * Kapanışa gönderme (plan M7 Task 21b; K24): koşullar domain'in `assertDefectClosable`'ıyla ekranda
 * da denetlenir [NR496 4.3.2d, 4.5.4d]; kayıt doğrulama adımına alınır ve açık onay adımıyla
 * gönderilir (§7; parola yalnız şirket ayarı açıkken, ADR 0075). Gönderilen içerik onaylanan
 * içeriktir.
 */
export function DefectCloseDialog({
  opened,
  defect,
  classEquipment,
  repair,
  master,
  onClose,
  onSubmit,
}: {
  opened: boolean;
  defect: DefectView;
  classEquipment: boolean;
  repair: RepairInfo | null;
  /** Part B'yi yazma sıfatı (ADR 0127 §2); eski sunucuda yok. */
  master?: MasterCapacity | undefined;
  onClose: () => void;
  /** Parola yalnız girildiyse. */
  onSubmit: (patch: DefectPatch, password: string | undefined) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [error, setError] = useState<unknown>(null);
  const reauth = useReauth(defect.kind === 'breakdown' ? 'breakdown_report' : 'defect_report');
  const schema = useMemo(
    () => closeFormSchema(defect, { repairReported: repair?.reported ?? false, classEquipment }),
    [defect, repair?.reported, classEquipment],
  );
  const form = useForm<CloseFormValues>({
    resolver: zodResolver(schema),
    defaultValues: closeFormOf(defect),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: pencere açılınca kurulur
  useEffect(() => {
    if (!opened) return;
    setError(null);
    reauth.reset();
    form.reset(closeFormOf(defect));
  }, [opened, defect.id, defect.rowVersion]);
  const submit = form.handleSubmit(async (values) => {
    if (reauth.missing) return;
    setError(null);
    try {
      await onSubmit(closePatchOf(defect, values), reauth.body().password);
    } catch (failure) {
      reauth.noteError(failure);
      setError(failure);
    }
  });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={t('pms.condition.defects.close.title', { no: defect.defectNo })}
      closeButtonProps={{ 'aria-label': t('pms.work.password.back') }}
    >
      <form onSubmit={submit} noValidate>
        <CloseFields
          form={form}
          defect={defect}
          classEquipment={classEquipment}
          repair={repair}
          reauth={reauth}
          master={master}
        />
        {error ? (
          <Alert mt="sm" color="red" icon={<IconAlertCircle size={16} />} role="alert">
            {errorText(error)}
          </Alert>
        ) : null}
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('pms.work.password.back')}
          </Button>
          <Button
            type="submit"
            leftSection={<IconSend size={16} />}
            loading={form.formState.isSubmitting}
            disabled={reauth.missing || partBMissing(defect, master)}
          >
            {t('pms.condition.defects.close.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
